import React, { FC } from "react";
import { Icon, IconId } from "src/components/icon";
import Circle from "src/components/spinner";
import styled, { css } from "styled-components";
import { Color, FontSize, theme } from "../config/theme";

const buttonStyles = {
  primary: css`
    background: ${theme.color.buttons.primary.background};

    :hover {
      background: ${theme.color.buttons.primary.hover};
    }

    :focus {
      outline: 2px solid ${theme.color.buttons.primary.focus};
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    }

    :active {
      background: ${theme.color.buttons.primary.active};
    }
  `,

  outlined: css`
    background: ${theme.color.white};
    outline:    1px solid ${theme.color.buttons.outlined.border};
    color:      ${theme.color.black};

    :hover {
      outline: 1px solid ${theme.color.buttons.outlined.hover};
    }

    :active {
      outline: 1px solid ${theme.color.buttons.outlined.active};
    }

    :focus {
      border: 1px solid ${theme.color.primary7};
      outline: 2px solid ${theme.color.buttons.outlined.focus};
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    }
  `,


  text: css<ButtonProps>`
    background:     transparent;
    border:         none;
    color:          ${(p) => `${theme.color[p.color]}` || `${theme.font.color.white}`};
    font-weight:    ${theme.font.weight.bold};
    text-transform: unset;

    :hover {
      background-color: ${theme.color.hover};
    }
  `,


  alert: css`
    background: ${theme.color.alert};

    :hover {
      background: ${theme.color.buttons.alert.hover};
    }

    :active {
      background: ${theme.color.buttons.alert.active};
    }

    :focus {
      outline: 2px solid ${theme.color.buttons.alert.focus};
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    }
  `,


  icon: css`
    background-color: transparent;
    color:            inherit;
    border-radius:    50%;
    transition:       0.3s ease-in-out;
    padding:          0.6rem 0.6rem;

    :hover {
      background-color:    ${theme.color.hover};
      background-position: fixed;
    }

    :active {
      transform: scale(0.92);
    }

    ::after {
      content:        "";
      position:       absolute;
      top:            50%;
      left:           50%;
      width:          150%;
      aspect-ratio:   1 / 1;
      transform:      translate(-50%, -50%) scale(0);
      pointer-events: none;
      border-radius:  99.9rem;
      background:     rgba(255, 255, 255, 0.5);
    }

    :focus::after {
      animation: scale_up 1000ms forwards;
    }

    @keyframes scale_up {
      0% {
        transform: translate(-50%, -50%) scale(0);
        opacity: 1;
      }
      100% {
        transform: translate(-50%, -50%) scale(1);
        opacity: 0;
      }
    }
  `
};

const buttonLoaderColors: {[key: string]: string} = {
  outlined:  theme.color.black,
  primary:   theme.color.white,
  secondary: theme.color.white,
  alert:     theme.color.white,
};

const buttonPaddings: {[key: string]: string} = {
  large:   "1.5rem 4rem",
  default: "0.75rem 2rem",
  small:   "0.5rem 1rem",
};

const buttonFontSizes = {
  large: css`
    ${theme.font.size.default}
  `,
  default: css`
    ${theme.font.size.small}
  `,
  small: css`
    ${theme.font.size.xsmall}
  `,
  xsmall: css`
    ${theme.font.size.xsmall}
  `,
  xxsmall: css`
  ${theme.font.size.xxsmall}
  `,
  xxxsmall: css`
  ${theme.font.size.xxsmall}
  `,
};

const StyledButton = styled.button<ButtonProps>`
  position:        relative;
  justify-content: center;
  text-align:      center;
  align-items:     center;
  display:         inline-flex;
  flex-direction:  row;
  text-transform:  uppercase;
  border:          none;
  cursor:          pointer;
  font-weight:     ${theme.font.weight.bold};
  color:           ${(p) => `${theme.font.color.white}` || `${theme.color[p.color]}`};
  border-radius:   4px;
  padding:         ${(p) => buttonPaddings[p.size]};
  ${(props) =>
    props.variant ? buttonStyles[props.variant] : buttonStyles["primary"]};
  font-size: ${(p) => buttonFontSizes[p.size]};
  ${p => p.iconPosition === 'left' && 'flex-direction: row-reverse;'};
  box-shadow: ${buttonStyles["icon"]
    ? `0 0 0 0`
    : `0 2px 15px 0 rgb(45 54 65 / 15%)`};
  min-width: fit-content;

  &:disabled {
    cursor:     not-allowed;
    background: ${theme.color.disabled};
    color:      ${theme.color.disabledText};
    opacity:    0.7;
    outline:    none;
    border:     none;
  }

  &:active {
    transform: translateY(0.5px);
  }
`;

const IconContainer = styled.span<{ iconPosition?: IconPosition }>`
  ${p =>
    p.iconPosition === 'left'
      ? css`
          margin-left: 0.85rem;
        `
        : css`
          margin-right: 0.85rem;
        `
        }
`;

type ButtonVariant = "primary"| "outlined" | "icon" | "text" | "alert";
type IconPosition  = 'left' | 'right';

type ButtonProps = {
  children?:     any;
  className?:    string;
  color?:        Color;
  disabled?:     boolean;
  hide?:         boolean;
  icon?:         IconId;
  isLoading?:    boolean;
  onClick?:      React.MouseEventHandler<HTMLButtonElement>;
  open?:         boolean;
  size?:         FontSize;
  variant?:      ButtonVariant;
  iconPosition?: IconPosition;
  iconId?:       IconId;
};

const InnerContent = styled.div`
  display:         flex;
  flex-direction:  row;
  position:        relative;
  align-items:     center;
  justify-content: center;
  gap:             0.5rem;
  opacity:         ${(p) => (p.hidden ? 0 : 1)};
`;

export const Button: FC<ButtonProps> = ({
  variant      = "primary",
  size         = "small",
  disabled     = false,
  isLoading    = false,
  iconPosition = "right",
  onClick,
  children,
  iconId,
  ...rest
}) => {
  return (
    <StyledButton
      {...rest}
      variant      = {variant}
      size         = {size}
      disabled     = {disabled}
      onClick      = {onClick}
      isLoading    = {isLoading}
      iconPosition = {iconPosition}
    >
      {iconId && !isLoading &&(
        <IconContainer iconPosition={iconPosition}>
          <Icon iconId={iconId} size="default" />
        </IconContainer>
      )}
      <InnerContent hidden={isLoading}>{children}</InnerContent>
      {isLoading && (
        <Circle
          size  = {Number(theme.font.size[size].replace("rem", "")) * 0.75}
          color = {buttonLoaderColors[variant]}
        />
      )}
    </StyledButton>
  );
};
